import { useState, useCallback, useMemo, useEffect } from "react";
import { StormDoorColor } from "../../../entities-generated/common/storm-door-color";
import { IStormDoorColorOption } from "../../../entities-generated/common/storm-door-color-option";
import { BwjInput } from "../../../framework/entities/bwj-input";
import { BwjValidityState } from "../../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../../framework/services/event-notification";
import { IFormValidityCheck, ImmutableFormValueService, ValidatedFormValueService } from "../../../framework/services/form-model-service-components";
import { NavigationService } from "../../../framework/services/navigation";
import { contentChanged, isNotNullOrWhitespace, isNullOrWhitespace } from "../../../framework/utils";
import { firstOrDefault, none, where } from "../../../framework/utils/collection";
import { ProductTextureEditingModel } from "../../entities/product-texture-editing-model";
import { StormDoorEvents } from "../events";
import { IVariantEditorFormModel, IVariantEditorValueModel, IVariantEditorModelChange } from "./variant-editor-iface";
import { IProductTexture, ProductTexture } from "../../../entities-generated/common/product-texture";

// ##BEGIN:DEVELOPERCODE##

// this is a good place for doing things like defining custom form value service types

// ##END:DEVELOPERCODE##

// %%BEGIN:id/immutable/string%%
export const id = new (class idService extends ImmutableFormValueService<ProductTextureEditingModel, string> {
    public initialize(data: ProductTextureEditingModel): void {
        this.value = data.id;
    }

    public applyToDataModel(data: ProductTextureEditingModel): void {
        data.id = this.value;
    }

    public getDefaultValue(): string {
        return '';
    }

    public getInputValue(): string {
        return this.value;
    }

})(
    'id' // id
    ,'' // description
);
// %%END:id/immutable/string%%

// %%BEGIN:name/validated/string/string%%
export const name = new (class nameService extends ValidatedFormValueService<ProductTextureEditingModel, string, string> {
    public initialize(data: ProductTextureEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.name));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: ProductTextureEditingModel): void {
        data.name = this.value.value;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter name of variant');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'name' // id
    ,'Name' // description
);
// %%END:name/validated/string/string%%

// %%BEGIN:swatchUrl/validated/string/string%%
export const swatchUrl = new (class swatchUrlService extends ValidatedFormValueService<ProductTextureEditingModel, string, string> {
    public initialize(data: ProductTextureEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.swatchUrl));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: ProductTextureEditingModel): void {
        data.swatchUrl = this.value.value;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'swatchUrl' // id
    ,'Color swatch' // description
);
// %%END:swatchUrl/validated/string/string%%

// %%BEGIN:productImageUrls/validated/string/string[]%%
export const productImageUrls = new (class productImageUrlsService extends ValidatedFormValueService<ProductTextureEditingModel, string, string[]> {
    public initialize(data: ProductTextureEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.productImageUrls.join('\n')));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: ProductTextureEditingModel): void {
        data.productImageUrls = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string[] {
        const value = this.value.value;
        const op = isNullOrWhitespace(value) ? [] : value.split('\n');
        const output = where(op, i => isNotNullOrWhitespace(i));
        return output;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'productImageUrls' // id
    ,'Variant-specific images' // description
);
// %%END:productImageUrls/validated/string/string[]%%

// %%BEGIN:colorFamily/validated/IStormDoorColorOption | null/StormDoorColor%%
export const colorFamily = new (class colorFamilyService extends ValidatedFormValueService<ProductTextureEditingModel, IStormDoorColorOption | null, StormDoorColor> {
    public initialize(data: ProductTextureEditingModel): void {
        this.options = data.colorFamilies;
        const cf = firstOrDefault(this.options, c => c.id === data.colorFamily);
        this.setInputValue(new BwjInput<IStormDoorColorOption | null>(cf));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: ProductTextureEditingModel): void {
        data.colorFamilies = this.options;
        data.colorFamily = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<IStormDoorColorOption | null> {
        return new BwjInput<IStormDoorColorOption | null>(null);
    }

    public getInputValue(): BwjInput<IStormDoorColorOption | null> {
        return this.value;
    }

    public setInputValue(value: BwjInput<IStormDoorColorOption | null>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): StormDoorColor {
        const value = this.value.value;
        return value?.id ?? StormDoorColor.Indeterminate;
    }

    public validate(value: IStormDoorColorOption | null): Promise<BwjInput<IStormDoorColorOption | null>> {
        const result = this.requiredFieldValidator(value, 'Select a color family');
        return Promise.resolve(result);
    }

    public onUpdate(value: IStormDoorColorOption | null): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

    public options: IStormDoorColorOption[] = [];
})(
    'colorFamily' // id
    ,'Color family' // description
);
// %%END:colorFamily/validated/IStormDoorColorOption | null/StormDoorColor%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): IVariantEditorFormModel => {
    return {
       name: name.getInputValue(),
       swatchUrl: swatchUrl.getInputValue(),
       productImageUrls: productImageUrls.getInputValue(),
       colorFamily: colorFamily.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): IVariantEditorValueModel => {
    return {
       id: id.getOutputValue(),
       name: name.getOutputValue(),
       swatchUrl: swatchUrl.getOutputValue(),
       productImageUrls: productImageUrls.getOutputValue(),
       colorFamily: colorFamily.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        name.getInputValue(),
        swatchUrl.getInputValue(),
        productImageUrls.getInputValue(),
        colorFamily.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: IVariantEditorModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(StormDoorEvents.VariantFormModelDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<IVariantEditorFormModel>> => {
    const promises = [
        name.formValidateAndUpdate(),
        swatchUrl.formValidateAndUpdate(),
        productImageUrls.formValidateAndUpdate(),
        colorFamily.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:IVariantEditorFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: IVariantEditorValueModel, dataModel: ProductTextureEditingModel } = {
    dataModel: new ProductTextureEditingModel(new ProductTexture(), [])
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: ProductTextureEditingModel) => {
    const dataModel = data || new ProductTextureEditingModel(new ProductTexture(), []);

    id.initialize(dataModel);
    name.initialize(dataModel);
    swatchUrl.initialize(dataModel);
    productImageUrls.initialize(dataModel);
    colorFamily.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:IVariantEditorFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
name.setChangePublisher(broadcastChange);
swatchUrl.setChangePublisher(broadcastChange);
productImageUrls.setChangePublisher(broadcastChange);
colorFamily.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = new ProductTextureEditingModel(new ProductTexture(), []);

    id.applyToDataModel(dataModel);
    name.applyToDataModel(dataModel);
    swatchUrl.applyToDataModel(dataModel);
    productImageUrls.applyToDataModel(dataModel);
    colorFamily.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: IVariantEditorModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], StormDoorEvents.VariantFormModelDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): IVariantEditorFormModel => {
    const [getter, setter] = useState<IVariantEditorFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: IVariantEditorModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: IVariantEditorFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('VariantEditorFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('VariantEditorFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

export const toProductTexture = (): IProductTexture => toValueModel();

// ##END:DEVELOPERCODE##
